import Highcharts from "highcharts/highstock";
import HighchartsReact from "highcharts-react-official";
import variablePie from "highcharts/modules/variable-pie.js";
import Typography from "@portal/components/atoms/v2/typography";
import useRiskLevelProposal from "@portal/hooks/robot/useRiskLevelProposal";
import { useEffect } from "react";
import { MainBox } from "../components/mainBox";
import { RiskLevelBarComponent } from "../components/riskLevelBar/RiskLevelBarComponent";
import { RiskLevelData } from "../components/highcharts/RiskLevelData";
import { GetRiskLevelName } from "../components/helpers/GetRiskLevelName";
import { IPageProps } from "@portal/types/robot/IPageProps";
import { RobotTextResources } from "@portal/assets/text/RobotTextResources";
import { useAppDispatch, useAppSelector } from "@portal/redux/store";
import { setRiskLevelResponse } from "@portal/redux/reducers/riskLevelReducer";
import { setAgreementRiskLevel, setCustomerNumber } from "@portal/redux/reducers/savingProposalReducer";
import { setCalculationsRiskLevel } from "@portal/redux/reducers/savingCalculationsReducer";
import { RobotFooterComponent } from "../components/footer";
import { TextLine } from "@portal/components/atoms/v2/textLine";
import { InfoBoxComponent } from "@portal/components/atoms/infoBox";
import useCustomerInfo from "@portal/hooks/useCustomerInfo";
import PageSpinner from "@portal/components/molecules/v2/pageSpinner";

export const RiskLevelPage = ({ centerId, pageIndex, isDefaultLanguage }: IPageProps) => {
  variablePie(Highcharts);
  const dispatch = useAppDispatch();
  const request = useAppSelector(state => state.persisted.riskLevel.request);
  const savingsAgreementCustomerNumber = useAppSelector(state => state.persisted.savingProposal.customerNumber);
  const response = useRiskLevelProposal(request);
  const { data: customerInfo, isLoading: customerInfoIsLoading } = useCustomerInfo();
  const hasError = response.error !== null && response.error !== undefined;

  const riskLevelProposal = response?.data ? response.data.find(x => x.default) : undefined;
  const name = GetRiskLevelName(riskLevelProposal?.riskLevel, isDefaultLanguage);
  const isLoading = response.isLoading;
  const texts = isDefaultLanguage ? RobotTextResources.primary : RobotTextResources.secondary;

  useEffect(() => {
    if (response) dispatch(setRiskLevelResponse(response));
    if (riskLevelProposal) {
      dispatch(setAgreementRiskLevel(riskLevelProposal.riskLevel));
      dispatch(setCalculationsRiskLevel(riskLevelProposal.riskLevel));
    }
  }, [response, riskLevelProposal, dispatch]);

  useEffect(() => {
    if (customerInfo?.contactInfo.customerNumber && !savingsAgreementCustomerNumber) {
      dispatch(setCustomerNumber(customerInfo?.contactInfo.customerNumber));
    }
  }, [customerInfo?.contactInfo.customerNumber, dispatch, savingsAgreementCustomerNumber]);

  return (
    <>
      <MainBox>
        <Typography variant="H6">
          {!isLoading && name ? `${texts.PAGE_12_SUBHEADER} ${name} ${texts.PAGE_12_SUBHEADER_B}` : texts.MESSAGE_LOADING}
        </Typography>
        <TextLine />
        <div className="flex flex-col mt-2">
          <RiskLevelBarComponent proposal={riskLevelProposal} isDefaultLanguage={isDefaultLanguage} />
          <div className="flex flex-col my-3 p-1 border rounded-md bg-white h-[400px]">
            {isLoading ? (
              <PageSpinner />
            ) : (
              <HighchartsReact highcharts={Highcharts} options={RiskLevelData(riskLevelProposal, isDefaultLanguage)} />
            )}
          </div>
        </div>
        {hasError && <InfoBoxComponent texts={[texts.ERROR_FETCH]} warning />}
      </MainBox>
      <RobotFooterComponent
        pageIndex={pageIndex}
        centerId={centerId}
        primaryButtonText={texts.BUTTON_NEXT}
        secondaryButtonText={texts.BUTTON_BACK}
        disabled={hasError || customerInfoIsLoading}
      />
    </>
  );
};
